/**
 * 规范化单个展位编号（兼容接口返回的带引号格式，如 "\"A01\""）
 * @param {string|number|null|undefined} value 展位编号
 * @returns {string}
 */
export function normalizeBoothNumber(value) {
    if (value == null || value === '') return '';
    const str = String(value).trim();
    try {
        const parsed = JSON.parse(str);
        if (typeof parsed === 'string') return parsed.trim();
    } catch (_) {}
    return str.replace(/^["']+|["']+$/g, '').trim();
}

/**
 * 是否为展览展位权益（isBoothBenefit=1）
 * @param {Object} benefit 权益行数据
 * @returns {boolean}
 */
export function isBoothBenefitType(benefit) {
    return benefit?.isBoothBenefit === 1;
}

/**
 * 权益池是否已配置可选展位号
 * @param {Object} benefit 权益行数据
 * @returns {boolean}
 */
export function hasAvailableBoothNumbers(benefit) {
    return Array.isArray(benefit?.availableBoothNumbers) && benefit.availableBoothNumbers.length > 0;
}

/**
 * 是否按展位选择流程处理（提交 quantity、校验 boothNumbers）
 * @param {Object} benefit 权益行数据
 * @returns {boolean}
 */
export function isBoothSelectType(benefit) {
    return isBoothBenefitType(benefit);
}

/** 展位下拉无配置时的占位选项 value（不可选中） */
export const BOOTH_EMPTY_OPTION_VALUE = '__no_booth__';

/**
 * 计算被其他机构占用的展位集合（排除本机构已保存的展位，含编辑时临时取消勾选的）
 * @param {Array<{ boothNumber?: string }>|null|undefined} soldBoothList 权益池已售展位列表
 * @param {string|string[]|null|undefined} ownedBoothNumbers 本机构已保存的展位（编辑回显初始值）
 * @returns {Set<string>}
 */
export function getOccupiedByOthersBoothSet(soldBoothList, ownedBoothNumbers) {
    const sold = parseSoldBoothList(soldBoothList);
    const ownedSet = new Set(parseBoothNumbers(ownedBoothNumbers));
    return new Set(sold.filter(num => !ownedSet.has(num)));
}

/**
 * 解析 soldBoothList，提取展位编号列表
 * @param {Array<{ boothNumber?: string }>|null|undefined} soldBoothList
 * @returns {string[]}
 */
export function parseSoldBoothList(soldBoothList) {
    if (!Array.isArray(soldBoothList)) return [];
    return soldBoothList.map(item => normalizeBoothNumber(item?.boothNumber ?? item)).filter(Boolean);
}

/**
 * 解析展位号字符串为数组，兼容 JSON 数组字符串（'["01","02"]'）和逗号拼接格式（"01,02"）
 * @param {string|string[]|null|undefined} boothNumbers 展位号
 * @returns {string[]}
 */
export function parseBoothNumbers(boothNumbers) {
    if (!boothNumbers) return [];
    if (Array.isArray(boothNumbers)) return boothNumbers.map(String).filter(Boolean);

    const str = String(boothNumbers).trim();
    if (!str) return [];

    if (str.startsWith('[')) {
        try {
            const parsed = JSON.parse(str);
            if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
        } catch (_) {}
    }

    return str
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
}

/**
 * 将展位号规范化为内部逗号拼接格式
 * @param {string|string[]|null|undefined} boothNumbers 展位号
 * @returns {string}
 */
export function normalizeBoothNumbers(boothNumbers) {
    return parseBoothNumbers(boothNumbers).join(',');
}

/**
 * 将展位号转换为提交接口所需的 JSON 数组字符串
 * @param {string|string[]|null|undefined} boothNumbers 展位号
 * @returns {string}
 */
export function formatBoothNumbersForApi(boothNumbers) {
    const arr = parseBoothNumbers(boothNumbers);
    return arr.length > 0 ? JSON.stringify(arr) : '';
}
